import { Avatar, type AvatarProps, Box, UnstyledButton } from '@mantine/core'
import type { Hue, Tone } from '@tnn/core'
import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react'
import { useLinkComponent } from '../link'
import type { IconComponent } from '../shell/Shell'
import classes from './components.module.css'

export interface PageProps {
  title: ReactNode
  subtitle?: ReactNode
  /** Controls next to the title (date picker, view switch…) */
  titleAside?: ReactNode
  /** Icon buttons and buttons at the right of the header */
  actions?: ReactNode
  /** Row under the header: filter chips, search, tabs */
  toolbar?: ReactNode
  /**
   * `sheet` (default): the page fills the white sheet and its sections are bands split by dividers.
   * `canvas`: dashboards; the sheet steps back and each section is its own white card.
   */
  surface?: 'sheet' | 'canvas'
  children: ReactNode
}

/** Standard page frame inside the content sheet. */
export function Page({ title, subtitle, titleAside, actions, toolbar, surface = 'sheet', children }: PageProps) {
  return (
    <section className={classes.page} data-surface={surface}>
      <header className={classes.pageHeader}>
        <div style={{ minWidth: 0 }}>
          <h1 className={classes.pageTitle}>{title}</h1>
          {subtitle && <div className={classes.pageSubtitle}>{subtitle}</div>}
        </div>
        {titleAside}
        {actions && <div className={classes.pageActions}>{actions}</div>}
      </header>
      {toolbar && <div className={classes.pageToolbar}>{toolbar}</div>}
      <div className={classes.pageBody}>{children}</div>
    </section>
  )
}

export function toneVars(tone: Tone): CSSProperties {
  return {
    '--pill-bg': `var(--tnn-tone-${tone}-bg)`,
    '--pill-fg': `var(--tnn-tone-${tone}-fg)`,
    '--pill-dot': `var(--tnn-tone-${tone}-dot)`,
  } as CSSProperties
}

/** A record's own colour (`Hue`) as a pill's: `--pill-bg`, `--pill-fg`, `--pill-dot` */
export function hueVars(hue: Hue): CSSProperties {
  return {
    '--pill-bg': `var(--tnn-hue-${hue}-bg)`,
    '--pill-fg': `var(--tnn-hue-${hue}-fg)`,
    '--pill-dot': `var(--tnn-hue-${hue}-dot)`,
  } as CSSProperties
}

export interface StatusPillProps {
  tone: Tone
  label: ReactNode
  icon?: IconComponent
  size?: 'sm' | 'md'
}

/** Tonal status pill with a dot, colour from the status tone. */
export function StatusPill({ tone, label, icon: Icon, size = 'md' }: StatusPillProps) {
  return (
    <span className={classes.pill} style={toneVars(tone)} data-size={size}>
      {Icon ? <Icon size={15} stroke={2} /> : <span className={classes.pillDot} />}
      {label}
    </span>
  )
}

const LEADING_VOWELS = /^[เแโใไ]/
const COMPANY_PREFIX = /^(บริษัท|บจก\.?|บมจ\.?|หจก\.?|ห้างหุ้นส่วนจำกัด|ห้างหุ้นส่วน|ร้าน)\s*/

/** First letter for an avatar, skipping Thai leading vowels and company prefixes. */
export function initialOf(name: string): string {
  const cleaned = name.trim().replace(COMPANY_PREFIX, '').replace(LEADING_VOWELS, '')
  return Array.from(cleaned)[0]?.toUpperCase() ?? '?'
}

/** Number of `--tnn-avatar-*` colours (theme/palette.ts `avatarColors`) */
const AVATAR_COUNT = 12

export function colorOf(seed: string): string {
  let hash = 0
  for (const ch of seed) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0
  return `var(--tnn-avatar-${(hash % AVATAR_COUNT) + 1})`
}

export interface InitialsAvatarProps extends Omit<AvatarProps, 'children' | 'color'> {
  name: string
}

/** Round avatar with the first letter of a person or company name. */
export function InitialsAvatar({ name, size = 30, ...rest }: InitialsAvatarProps) {
  return (
    <Avatar
      size={size}
      radius="xl"
      {...rest}
      styles={{ placeholder: { background: colorOf(name), color: 'var(--tnn-on-avatar)', fontWeight: 500 } }}
    >
      {initialOf(name)}
    </Avatar>
  )
}

export interface EmptyStateProps {
  icon: IconComponent
  title: ReactNode
  body?: ReactNode
  actions?: ReactNode
}

export function EmptyState({ icon: Icon, title, body, actions }: EmptyStateProps) {
  return (
    <div className={classes.empty}>
      <span className={classes.emptyIcon}>
        <Icon size={30} stroke={1.6} />
      </span>
      <div className={classes.emptyTitle}>{title}</div>
      {body && <div className={classes.emptyBody}>{body}</div>}
      {actions && <Box mt="sm">{actions}</Box>}
    </div>
  )
}

export function SectionCard({ title, children, id }: { title?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className={classes.section} id={id} data-tnn-section>
      {title && <h2 className={classes.sectionTitle}>{title}</h2>}
      {children}
    </section>
  )
}

/**
 * Sections side by side on wide screens (a dashboard's two lists), stacked on narrow ones: on a sheet they are bands
 * under one hairline with a gutter between, each as tall as its own content
 */
export function SectionRow({ children }: { children: ReactNode }) {
  return (
    <div className={classes.sectionRow} data-tnn-section-row>
      {children}
    </div>
  )
}

export function KeyValueList({ items }: { items: { label: ReactNode; value: ReactNode; key: string }[] }) {
  return (
    <dl className={classes.kv}>
      {items.map((i) => (
        <div key={i.key} style={{ display: 'contents' }}>
          <dt>{i.label}</dt>
          <dd>{i.value ?? '–'}</dd>
        </div>
      ))}
    </dl>
  )
}

export interface StatProps {
  label: ReactNode
  value: ReactNode
  tone?: 'default' | 'danger'
  href?: string
  /** A figure on the page itself, with no tile behind it (an overview's row of counts) */
  plain?: boolean
}

export function Stat({ label, value, tone = 'default', href, plain }: StatProps) {
  const Link = useLinkComponent()
  return (
    <UnstyledButton
      component={(href ? Link : 'div') as 'a'}
      href={href}
      className={classes.stat}
      data-tone={tone}
      data-plain={plain || undefined}
    >
      <span className={classes.statLabel}>{label}</span>
      <span className={classes.statValue}>{value}</span>
    </UnstyledButton>
  )
}

export interface FilterChipOption {
  value: string
  label: ReactNode
  count?: number
  /** Status tone: a dot when not chosen, the status pill's colour when chosen */
  tone?: Tone
  /** A record's own colour, as a tone is worn (a kind of leave) */
  hue?: Hue
  /** In place of the dot, in the chip's colour: what the choice is known by */
  icon?: IconComponent
  /** Not to be chosen now (a kind of leave one has no right to yet) */
  disabled?: boolean
}

/** Single-choice filter chips, Material style, with counts. `wrap`: onto more lines rather than scrolled sideways (a dialog's choice) */
export function FilterChips({
  options,
  value,
  onChange,
  label,
  wrap,
}: {
  options: FilterChipOption[]
  value: string
  onChange: (v: string) => void
  /** What is chosen, for screen readers */
  label?: string
  wrap?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  // A chip chosen from a link may sit past the scrolled edge on phones
  useEffect(() => {
    const row = ref.current
    const chip = row?.querySelector<HTMLElement>(`[data-value="${CSS.escape(value)}"]`)
    if (!row || !chip) return
    const left = chip.offsetLeft - row.offsetLeft
    const hidden = left < row.scrollLeft || left + chip.offsetWidth > row.scrollLeft + row.clientWidth
    if (hidden) row.scrollTo({ left: left - 16, behavior: 'smooth' })
  }, [value])
  // Fade the edge that has more chips past it
  useEffect(() => {
    const row = ref.current
    if (!row) return
    const update = () => {
      const start = row.scrollLeft > 1
      const end = row.scrollLeft + row.clientWidth < row.scrollWidth - 1
      row.dataset.fade = start && end ? 'both' : start ? 'start' : end ? 'end' : ''
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(row)
    row.addEventListener('scroll', update, { passive: true })
    return () => {
      observer.disconnect()
      row.removeEventListener('scroll', update)
    }
  }, [])
  return (
    <div className={classes.chips} role="radiogroup" aria-label={label} data-wrap={wrap || undefined} ref={ref}>
      {options.map((o) => {
        const selected = o.value === value
        return (
          <UnstyledButton
            key={o.value}
            className={classes.chip}
            data-value={o.value}
            data-selected={selected || undefined}
            data-tone={o.tone}
            data-hue={o.hue}
            style={o.hue ? hueVars(o.hue) : o.tone ? toneVars(o.tone) : undefined}
            role="radio"
            aria-checked={selected}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
          >
            {selected ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="m5 12 5 5 9-10" />
              </svg>
            ) : o.icon ? (
              <o.icon size={16} className={classes.chipIcon} aria-hidden />
            ) : (
              o.tone && <span className={classes.pillDot} />
            )}
            {o.label}
            {o.count != null && <span className={classes.chipCount}>{o.count}</span>}
          </UnstyledButton>
        )
      })}
    </div>
  )
}

export interface RecordCardProps {
  title: ReactNode
  subtitle?: ReactNode
  status?: ReactNode
  meta?: ReactNode[]
  avatar?: ReactNode
  href?: string
  onClick?: () => void
}

/** One record as a card: how lists render on phones. */
export function RecordCard({ title, subtitle, status, meta, avatar, href, onClick }: RecordCardProps) {
  const Link = useLinkComponent()
  return (
    <UnstyledButton component={(href ? Link : 'button') as 'a'} href={href} onClick={onClick} className={classes.card}>
      <div className={classes.cardTop}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {avatar}
          <div style={{ minWidth: 0 }}>
            <div className={classes.cardTitle}>{title}</div>
            {subtitle && <div className={classes.cardSubtitle}>{subtitle}</div>}
          </div>
        </div>
        {status}
      </div>
      {meta && meta.length > 0 && (
        <div className={classes.cardMeta}>
          {meta.map((m, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: meta items are positional
            <span key={i}>{m}</span>
          ))}
        </div>
      )}
    </UnstyledButton>
  )
}

export function RecordCards({ children }: { children: ReactNode }) {
  return <div className={classes.cards}>{children}</div>
}
